/**
 * Spatial index for the board (docs/03-ARCHITECTURE.md §7.4).
 *
 * Two jobs, both about not looking at every stroke on the board:
 *  - culling: which strokes are actually on screen and worth drawing
 *  - hit-testing: which strokes are under the eraser or the lasso (Phase 4)
 *
 * A board can hold tens of thousands of strokes after a three-hour lesson, and a
 * linear scan of those on every frame is what a 4 GB Chromebook cannot afford.
 */

import { rectsIntersect, type Rect } from '@opal/shared';

export interface Boxed {
  readonly id: string;
  readonly bounds: Rect;
}

export interface Quadtree {
  insert(item: Boxed): void;
  /** Everything whose box touches this area. May include a few near misses. */
  query(area: Rect): Boxed[];
  readonly size: number;
}

const MAX_ITEMS_PER_NODE = 8;
const MAX_DEPTH = 8;

interface Node {
  readonly bounds: Rect;
  readonly depth: number;
  readonly items: Boxed[];
  children: Node[] | null;
}

function makeNode(bounds: Rect, depth: number): Node {
  return { bounds, depth, items: [], children: null };
}

function contains(outer: Rect, inner: Rect): boolean {
  return (
    inner.x >= outer.x &&
    inner.y >= outer.y &&
    inner.x + inner.width <= outer.x + outer.width &&
    inner.y + inner.height <= outer.y + outer.height
  );
}

function split(node: Node): Node[] {
  const halfWidth = node.bounds.width / 2;
  const halfHeight = node.bounds.height / 2;
  const { x, y } = node.bounds;
  return [
    makeNode({ x, y, width: halfWidth, height: halfHeight }, node.depth + 1),
    makeNode({ x: x + halfWidth, y, width: halfWidth, height: halfHeight }, node.depth + 1),
    makeNode({ x, y: y + halfHeight, width: halfWidth, height: halfHeight }, node.depth + 1),
    makeNode(
      { x: x + halfWidth, y: y + halfHeight, width: halfWidth, height: halfHeight },
      node.depth + 1,
    ),
  ];
}

function insertInto(node: Node, item: Boxed): void {
  if (node.depth >= MAX_DEPTH) {
    node.items.push(item);
    return;
  }
  node.children ??= node.items.length >= MAX_ITEMS_PER_NODE ? split(node) : null;
  const child = node.children?.find((candidate) => contains(candidate.bounds, item.bounds));
  if (child) {
    insertInto(child, item);
    return;
  }
  // Straddles a boundary, so it belongs to this node rather than any one child.
  node.items.push(item);
}

function collect(node: Node, area: Rect, found: Boxed[]): void {
  if (!rectsIntersect(node.bounds, area)) return;
  for (const item of node.items) {
    if (rectsIntersect(item.bounds, area)) found.push(item);
  }
  for (const child of node.children ?? []) collect(child, area, found);
}

/**
 * Creates an index over a fixed region.
 *
 * The canvas is infinite, so anything landing outside the region is simply kept in a
 * short list that every query checks. Work that far out is rare, and correctness
 * matters more than the speed of the exception.
 */
export function createQuadtree(bounds: Rect): Quadtree {
  const root = makeNode(bounds, 0);
  const outside: Boxed[] = [];
  let count = 0;

  return {
    get size() {
      return count;
    },
    insert(item) {
      count += 1;
      if (contains(bounds, item.bounds)) insertInto(root, item);
      else outside.push(item);
    },
    query(area) {
      const found: Boxed[] = [];
      collect(root, area, found);
      for (const item of outside) {
        if (rectsIntersect(item.bounds, area)) found.push(item);
      }
      return found;
    },
  };
}
